import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Flex, Input, Select, Switch, Textarea } from '@molecule/app-ui-react'

import { AGENT_NAME } from '../branding.js'
import {
  addHook,
  type AutomationState,
  AUTORUN_EVENTS,
  cancelScheduledTask,
  draftFrom,
  draftProblem,
  emptyDraft,
  formatDuration,
  type HookDraft,
  type HookEvent,
  type HookFeedback,
  hookRef,
  type HookRun,
  type HooksHttpClient,
  type HooksState,
  type HookType,
  type ListedBuiltIn,
  type ListedHook,
  loadAutomation,
  loadHooks,
  removeHook,
  runHookNow,
  type ScheduledTaskRow,
  setHookEnabled,
  updateHook,
} from '../lib/projectHooks.js'
import { Chip, type ChipTone, EnvRow, SectionHeading } from './EnvironmentPanel.js'
import { MoleculeSpinner } from './MoleculeSpinner.js'
import { MutedNote, PanelNotice } from './PanelNotice.js'

/**
 * The Autoruns panel — everything that runs by itself in a project, in one
 * place: the hooks and autoruns (add, edit, switch on/off, run now, last
 * result), what is running right now (background commands and subagents of
 * live turns, active sessions), and scheduled tasks.
 *
 * An AUTORUN is a hook the platform runs when something happens — after a
 * deploy goes live, when a background task finishes, when {@link AGENT_NAME}
 * finishes a turn, when a file changes — running a shell command, the project's
 * checks, or a background agent, and handing the result back to the builder
 * (on failure by default). Static sites ship with one: `post-deploy-e2e`.
 * Tool hooks (before/after a tool) live in the same files and are listed too.
 *
 * Every edit goes through `/projects/:id/hooks` (api `hooks-handlers.ts`),
 * which writes the SAME sandbox files `/hooks` and the builder's
 * `manage_hooks` tool edit — so the three can never disagree. The hook files
 * live in the sandbox, so the hooks section needs it running; what is running
 * and the scheduled tasks come from the platform and show regardless.
 *
 * Polls every 5 s while open and the tab is visible.
 */

/** Poll cadence while the panel is open. */
const POLL_MS = 5_000

/** Props for {@link AutorunsPanel}. */
export interface AutorunsPanelProps {
  projectId: string
  /** Editor+ — adds, edits, switches and runs. Viewers see everything read-only. */
  canEdit: boolean
  /** Whether the dev sandbox is up (the hook files live in it). */
  sandboxRunning: boolean
  /** Starts the sandbox (the notice's action while it sleeps). */
  onWakeSandbox?: () => void
}

/** Default English label per event (the row's "Runs" line and the event picker). */
const EVENT_LABELS: Record<HookEvent, string> = {
  PostDeploy: 'After a deploy goes live',
  TaskEnd: 'When a background task finishes',
  Stop: `When ${AGENT_NAME} finishes a turn`,
  FileChanged: 'When a file changes',
  PreToolUse: 'Before a tool runs (can stop it)',
  PostToolUse: 'After a tool runs',
}

/** What the matcher narrows, per event (the field's label). */
const MATCHER_LABELS: Record<HookEvent, string> = {
  PostDeploy: '',
  Stop: '',
  TaskEnd: 'Only tasks whose command matches (regex, optional)',
  FileChanged: 'Only files whose path matches (regex, optional)',
  PreToolUse: 'Tools it applies to (regex, e.g. write_file|edit_file)',
  PostToolUse: 'Tools it applies to (regex, e.g. write_file|edit_file)',
}

/** Default English label per feedback mode. */
const FEEDBACK_LABELS: Record<HookFeedback, string> = {
  'on-failure': `Tell ${AGENT_NAME} when it fails`,
  always: `Always tell ${AGENT_NAME} the result`,
  never: 'Only show it here',
}

/** The chip for a hook's state + last run. */
function statusChip(
  t: ReturnType<typeof useTranslation>['t'],
  enabled: boolean,
  run: HookRun | undefined,
  molId: string,
): ReactNode {
  if (!enabled) {
    return (
      <Chip tone="none" molId={molId}>
        {t('autoruns.status.off', undefined, { defaultValue: 'Off' })}
      </Chip>
    )
  }
  if (!run) {
    return (
      <Chip tone="none" molId={molId}>
        {t('autoruns.status.notRun', undefined, { defaultValue: 'Not run yet' })}
      </Chip>
    )
  }
  const tone: ChipTone =
    run.status === 'passed' ? 'configured' : run.status === 'started' ? 'byo' : 'warning'
  const label =
    run.status === 'passed'
      ? t('autoruns.status.passed', undefined, { defaultValue: 'Passed' })
      : run.status === 'failed'
        ? t('autoruns.status.failed', undefined, { defaultValue: 'Failed' })
        : run.status === 'started'
          ? t('autoruns.status.started', undefined, { defaultValue: 'Agent started' })
          : t('autoruns.status.error', undefined, { defaultValue: 'Could not run' })
  return (
    <Chip tone={tone} molId={molId}>
      {label}
    </Chip>
  )
}

/** One labelled line inside an expanded row. */
function Line({ label, children }: { label: string; children: ReactNode }) {
  const cm = getClassMap()
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 13, lineHeight: 1.5, marginBottom: 6 }}>
      <span className={cm.textMuted} style={{ flex: '0 0 96px' }}>
        {label}
      </span>
      <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{children}</span>
    </div>
  )
}

/** Monospace inline value (a command, a regex). */
function Mono({ children }: { children: ReactNode }) {
  return <code style={{ fontFamily: 'monospace', fontSize: 12 }}>{children}</code>
}

/** A run's output, scrollable. */
function RunOutput({ run, molId }: { run: HookRun; molId: string }) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const when = new Date(run.at)
  return (
    <div data-mol-id={molId} style={{ marginTop: 8 }}>
      <div className={cm.textMuted} style={{ fontSize: 12, marginBottom: 4 }}>
        {t(
          'autoruns.lastRun',
          {
            when: Number.isNaN(when.getTime()) ? run.at : when.toLocaleString(),
            duration: formatDuration(run.durationMs),
          },
          { defaultValue: 'Last run {{when}} · took {{duration}}' },
        )}
        {run.fedBack
          ? ` · ${t('autoruns.fedBack', { agentName: AGENT_NAME }, { defaultValue: 'sent to {{agentName}}' })}`
          : ''}
      </div>
      {run.output && (
        <pre
          style={{
            margin: 0,
            maxHeight: 200,
            overflow: 'auto',
            fontSize: 11,
            lineHeight: 1.45,
            padding: 8,
            borderRadius: 6,
            background: 'var(--color-surface-secondary, var(--color-surface))',
            border: '1px solid var(--color-border)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {run.output}
        </pre>
      )}
    </div>
  )
}

/** What a hook does, in one line. */
function doesText(
  t: ReturnType<typeof useTranslation>['t'],
  hook: { type: HookType; command?: string; steps?: string[]; prompt?: string },
): ReactNode {
  if (hook.type === 'check') {
    const steps = hook.steps?.length ? hook.steps.join(', ') : 'all'
    return t(
      'autoruns.does.check',
      { steps },
      { defaultValue: 'Runs the project checks: {{steps}}' },
    )
  }
  if (hook.type === 'agent') {
    return (
      <>
        {t('autoruns.does.agent', undefined, { defaultValue: 'Starts an agent with the task: ' })}
        {hook.prompt}
      </>
    )
  }
  return <Mono>{hook.command}</Mono>
}

/** The add / edit form. */
function HookForm({
  draft,
  onChange,
  checkSteps,
  editing,
  busy,
  error,
  onSave,
  onCancel,
}: {
  draft: HookDraft
  onChange: (next: HookDraft) => void
  checkSteps: string[]
  editing: boolean
  busy: boolean
  error: string | null
  onSave: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const cm = getClassMap()
  const isAutorun = AUTORUN_EVENTS.includes(draft.event)
  const problem = draftProblem(draft)
  const set = <K extends keyof HookDraft>(key: K, value: HookDraft[K]): void =>
    onChange({ ...draft, [key]: value })
  const eventOptions = (Object.keys(EVENT_LABELS) as HookEvent[]).map((e) => ({
    value: e,
    label: t(`autoruns.event.${e}`, { agentName: AGENT_NAME }, { defaultValue: EVENT_LABELS[e] }),
  }))
  const typeOptions = [
    {
      value: 'check',
      label: t('autoruns.type.check', undefined, { defaultValue: 'Run the project checks' }),
    },
    {
      value: 'command',
      label: t('autoruns.type.command', undefined, { defaultValue: 'Run a shell command' }),
    },
    {
      value: 'agent',
      label: t('autoruns.type.agent', undefined, { defaultValue: 'Start an agent' }),
    },
  ]
  const feedbackOptions = (Object.keys(FEEDBACK_LABELS) as HookFeedback[]).map((f) => ({
    value: f,
    label: t(
      `autoruns.feedback.${f}`,
      { agentName: AGENT_NAME },
      {
        defaultValue: FEEDBACK_LABELS[f],
      },
    ),
  }))
  return (
    <div
      data-mol-id="autoruns-form"
      style={{
        border: '1px solid var(--color-border)',
        borderRadius: 6,
        padding: 12,
        marginBottom: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <Select
        label={t('autoruns.form.when', undefined, { defaultValue: 'When' })}
        size="sm"
        data-mol-id="autoruns-form-event"
        value={draft.event}
        disabled={editing}
        options={eventOptions}
        onValueChange={(v) => {
          const event = v as HookEvent
          onChange({
            ...draft,
            event,
            // Tool hooks only run shell commands.
            type: AUTORUN_EVENTS.includes(event) ? draft.type : 'command',
          })
        }}
      />
      {MATCHER_LABELS[draft.event] && (
        <Input
          label={t(`autoruns.form.matcher.${draft.event}`, undefined, {
            defaultValue: MATCHER_LABELS[draft.event],
          })}
          size="sm"
          align="left"
          data-mol-id="autoruns-form-matcher"
          value={draft.matcher}
          onChange={(e) => set('matcher', (e.target as HTMLInputElement).value)}
        />
      )}
      {isAutorun && (
        <Select
          label={t('autoruns.form.does', undefined, { defaultValue: 'Does' })}
          size="sm"
          data-mol-id="autoruns-form-type"
          value={draft.type}
          options={typeOptions}
          onValueChange={(v) => set('type', v as HookType)}
        />
      )}
      {draft.type === 'command' && (
        <Input
          label={t('autoruns.form.command', undefined, {
            defaultValue: 'Command (runs in /workspace)',
          })}
          size="sm"
          align="left"
          data-mol-id="autoruns-form-command"
          placeholder="npm run lint"
          value={draft.command}
          onChange={(e) => set('command', (e.target as HTMLInputElement).value)}
        />
      )}
      {draft.type === 'check' && (
        <div data-mol-id="autoruns-form-steps">
          <div className={cm.textMuted} style={{ fontSize: 12, marginBottom: 6 }}>
            {t('autoruns.form.steps', undefined, { defaultValue: 'Checks to run' })}
          </div>
          <Flex gap="md" wrap="wrap">
            {checkSteps.map((step) => (
              <Switch
                key={step}
                size="sm"
                data-mol-id={`autoruns-form-step-${step}`}
                label={step}
                checked={draft.steps.includes(step)}
                onChange={() =>
                  set(
                    'steps',
                    draft.steps.includes(step)
                      ? draft.steps.filter((s) => s !== step)
                      : checkSteps.filter((s) => s === step || draft.steps.includes(s)),
                  )
                }
              />
            ))}
          </Flex>
        </div>
      )}
      {draft.type === 'agent' && (
        <Textarea
          label={t('autoruns.form.prompt', undefined, {
            defaultValue: 'Task for the agent (what to check and how to report)',
          })}
          size="sm"
          data-mol-id="autoruns-form-prompt"
          minRows={3}
          value={draft.prompt}
          onChange={(e) => set('prompt', (e.target as HTMLTextAreaElement).value)}
        />
      )}
      {isAutorun && (
        <Select
          label={t('autoruns.form.feedback', undefined, { defaultValue: 'Result' })}
          size="sm"
          data-mol-id="autoruns-form-feedback"
          value={draft.feedback}
          options={feedbackOptions}
          onValueChange={(v) => set('feedback', v as HookFeedback)}
        />
      )}
      <Input
        label={t('autoruns.form.name', undefined, { defaultValue: 'Name (optional)' })}
        size="sm"
        align="left"
        data-mol-id="autoruns-form-name"
        value={draft.name}
        onChange={(e) => set('name', (e.target as HTMLInputElement).value)}
      />
      {error && (
        <PanelNotice tone="error" molId="autoruns-form-error">
          {error}
        </PanelNotice>
      )}
      <Flex gap="sm" justify="end">
        <Button
          size="sm"
          variant="ghost"
          className={cm.touchTargetCompact}
          data-mol-id="autoruns-form-cancel"
          onClick={onCancel}
        >
          {t('common.cancel', undefined, { defaultValue: 'Cancel' })}
        </Button>
        <Button
          size="sm"
          color="primary"
          className={cm.touchTargetCompact}
          data-mol-id="autoruns-form-save"
          disabled={busy || problem !== null}
          onClick={onSave}
        >
          {editing
            ? t('autoruns.form.saveEdit', undefined, { defaultValue: 'Save' })
            : t('autoruns.form.add', undefined, { defaultValue: 'Add autorun' })}
        </Button>
      </Flex>
    </div>
  )
}

/**
 * The Autoruns panel.
 * @param root0 - Component props.
 * @param root0.projectId - The project.
 * @param root0.canEdit - Editor+ may change things.
 * @param root0.sandboxRunning - Whether the hook files can be read.
 * @param root0.onWakeSandbox - Starts the sandbox.
 * @returns The rendered panel.
 */
export function AutorunsPanel({
  projectId,
  canEdit,
  sandboxRunning,
  onWakeSandbox,
}: AutorunsPanelProps) {
  const { t } = useTranslation()
  const cm = getClassMap()
  const http = useHttpClient() as unknown as HooksHttpClient
  const [hooks, setHooks] = useState<HooksState | null>(null)
  const [automation, setAutomation] = useState<
    (AutomationState & { scheduled: ScheduledTaskRow[] }) | null
  >(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [form, setForm] = useState<{ draft: HookDraft; editing: string | null } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const mounted = useRef(true)

  const errorText = (error: unknown, fallback: string): string => {
    const data = (error as { response?: { data?: { error?: unknown } } })?.response?.data
    return typeof data?.error === 'string' ? data.error : fallback
  }

  const refresh = useCallback(async () => {
    try {
      const [h, a] = await Promise.all([
        sandboxRunning ? loadHooks(http, projectId) : Promise.resolve(null),
        loadAutomation(http, projectId),
      ])
      if (!mounted.current) return
      setHooks(h)
      setAutomation(a)
      setLoadError(null)
    } catch (error) {
      if (mounted.current) {
        setLoadError(
          errorText(
            error,
            t('autoruns.loadFailed', undefined, { defaultValue: 'Could not load autoruns.' }),
          ),
        )
      }
    }
  }, [http, projectId, sandboxRunning])

  useEffect(() => {
    mounted.current = true
    void refresh()
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void refresh()
    }, POLL_MS)
    return () => {
      mounted.current = false
      clearInterval(timer)
    }
  }, [refresh])

  /** Run one action, then refresh; errors show on the panel. */
  const act = async (key: string, fn: () => Promise<unknown>): Promise<void> => {
    setBusy(key)
    try {
      await fn()
      await refresh()
    } catch (error) {
      setLoadError(
        errorText(
          error,
          t('autoruns.actionFailed', undefined, { defaultValue: 'That did not work.' }),
        ),
      )
    } finally {
      if (mounted.current) setBusy(null)
    }
  }

  const saveForm = async (): Promise<void> => {
    if (!form) return
    setBusy('form')
    setFormError(null)
    try {
      if (form.editing) await updateHook(http, projectId, form.editing, form.draft)
      else await addHook(http, projectId, form.draft)
      setForm(null)
      await refresh()
    } catch (error) {
      setFormError(
        errorText(
          error,
          t('autoruns.saveFailed', undefined, { defaultValue: 'Could not save it.' }),
        ),
      )
    } finally {
      if (mounted.current) setBusy(null)
    }
  }

  const rows = useMemo(() => {
    if (!hooks) return { autoruns: [], tools: [] }
    type Row =
      | { kind: 'builtIn'; key: string; item: ListedBuiltIn }
      | { kind: 'user'; key: string; item: ListedHook }
    const all: Row[] = [
      ...hooks.builtIns
        .filter((b) => b.overriddenBy === undefined || !b.enabled)
        .map((b) => ({ kind: 'builtIn' as const, key: `builtin-${b.name}`, item: b })),
      ...hooks.hooks
        // A plain "switched off" override is shown as the built-in's own row.
        .filter((h) => !hooks.builtIns.some((b) => b.overriddenBy === h.id && !b.enabled))
        .map((h) => ({ kind: 'user' as const, key: `hook-${h.id}`, item: h })),
    ]
    return {
      autoruns: all.filter((r) => AUTORUN_EVENTS.includes(r.item.event)),
      tools: all.filter((r) => !AUTORUN_EVENTS.includes(r.item.event)),
    }
  }, [hooks])

  const renderRow = (row: (typeof rows.autoruns)[number]): ReactNode => {
    const { item } = row
    const name =
      item.name ??
      `${t(`autoruns.event.${item.event}`, { agentName: AGENT_NAME }, { defaultValue: EVENT_LABELS[item.event] })}`
    const run = item.name ? hooks?.lastRuns[item.name] : undefined
    const ref = row.kind === 'builtIn' ? encodeURIComponent(item.name ?? '') : hookRef(row.item)
    const isAutorun = AUTORUN_EVENTS.includes(item.event)
    const canSwitch = row.kind === 'user' || row.item.canDisable
    const molKey = (item.name ?? String((item as ListedHook).id)).replace(/[^a-z0-9-]/gi, '-')
    return (
      <EnvRow
        key={row.key}
        molId={`autoruns-row-${molKey}`}
        icon={isAutorun ? 'zap' : 'terminal'}
        label={name}
        packageName={
          row.kind === 'builtIn'
            ? t('autoruns.builtIn', undefined, { defaultValue: 'built-in' })
            : undefined
        }
        chip={statusChip(t, item.enabled, run, `autoruns-chip-${molKey}`)}
        expanded={expanded === row.key}
        onToggle={() => setExpanded((cur) => (cur === row.key ? null : row.key))}
      >
        <Line label={t('autoruns.runs', undefined, { defaultValue: 'Runs' })}>
          {t(
            `autoruns.event.${item.event}`,
            { agentName: AGENT_NAME },
            {
              defaultValue: EVENT_LABELS[item.event],
            },
          )}
          {item.matcher ? (
            <>
              {' · '}
              <Mono>{item.matcher}</Mono>
            </>
          ) : null}
        </Line>
        <Line label={t('autoruns.does', undefined, { defaultValue: 'Does' })}>
          {doesText(t, item)}
        </Line>
        {isAutorun && (
          <Line label={t('autoruns.result', undefined, { defaultValue: 'Result' })}>
            {t(
              `autoruns.feedback.${item.feedback ?? 'on-failure'}`,
              { agentName: AGENT_NAME },
              {
                defaultValue: FEEDBACK_LABELS[item.feedback ?? 'on-failure'],
              },
            )}
          </Line>
        )}
        {row.kind === 'builtIn' && !row.item.canDisable && (
          <MutedNote molId={`autoruns-note-${molKey}`} style={{ marginTop: 4 }}>
            {t('autoruns.alwaysOn', undefined, {
              defaultValue:
                'Always on: it stops shell commands that would lose work git cannot restore.',
            })}
          </MutedNote>
        )}
        {run && <RunOutput run={run} molId={`autoruns-output-${molKey}`} />}
        {canEdit && (
          <Flex gap="sm" wrap="wrap" align="center" style={{ marginTop: 10 }}>
            {canSwitch && (
              <Switch
                size="sm"
                data-mol-id={`autoruns-switch-${molKey}`}
                label={t('autoruns.on', undefined, { defaultValue: 'On' })}
                checked={item.enabled}
                disabled={busy !== null}
                onChange={() =>
                  void act(`switch-${row.key}`, () =>
                    setHookEnabled(http, projectId, ref, !item.enabled),
                  )
                }
              />
            )}
            <span style={{ flex: 1 }} />
            {isAutorun && item.enabled && (
              <Button
                size="sm"
                variant="outline"
                className={cm.touchTargetCompact}
                data-mol-id={`autoruns-run-${molKey}`}
                loading={busy === `run-${row.key}`}
                disabled={busy !== null}
                onClick={() => void act(`run-${row.key}`, () => runHookNow(http, projectId, ref))}
              >
                {t('autoruns.runNow', undefined, { defaultValue: 'Run now' })}
              </Button>
            )}
            {row.kind === 'user' && (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  className={cm.touchTargetCompact}
                  data-mol-id={`autoruns-edit-${molKey}`}
                  disabled={busy !== null}
                  onClick={() => {
                    setFormError(null)
                    setForm({ draft: draftFrom(row.item), editing: hookRef(row.item) })
                  }}
                >
                  {t('autoruns.edit', undefined, { defaultValue: 'Edit' })}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  color="error"
                  className={cm.touchTargetCompact}
                  data-mol-id={`autoruns-remove-${molKey}`}
                  disabled={busy !== null}
                  onClick={() =>
                    void act(`remove-${row.key}`, () =>
                      removeHook(http, projectId, hookRef(row.item)),
                    )
                  }
                >
                  {t('autoruns.remove', undefined, { defaultValue: 'Remove' })}
                </Button>
              </>
            )}
          </Flex>
        )}
      </EnvRow>
    )
  }

  const running = automation?.running ?? []
  const liveSessions = (automation?.sessions ?? []).filter((s) => s.active)
  const scheduled = (automation?.scheduled ?? []).filter(
    (s) => s.status === 'queued' || s.status === 'running',
  )

  return (
    // Padding here, not on .mol-panel-modal-body — the body is shared and each
    // panel pads its own root (the Storage/Activity idiom). Covers both
    // presentations: the desktop dialog and the phone's full-screen sheet.
    <div data-mol-id="autoruns-panel" style={{ padding: 16, fontSize: 13 }}>
      <MutedNote molId="autoruns-intro">
        {t(
          'autoruns.intro',
          { agentName: AGENT_NAME },
          {
            defaultValue:
              'Checks the platform runs by itself when something happens, and tells {{agentName}} the result.',
          },
        )}
      </MutedNote>
      {loadError && (
        <PanelNotice tone="error" molId="autoruns-error">
          {loadError}
        </PanelNotice>
      )}

      <Flex align="center" justify="between" style={{ marginBottom: 8 }}>
        <SectionHeading molId="autoruns-heading" first>
          {t('autoruns.heading', undefined, { defaultValue: 'Autoruns' })}
        </SectionHeading>
        {canEdit && sandboxRunning && !form && (
          <Button
            size="sm"
            color="primary"
            className={cm.touchTargetCompact}
            data-mol-id="autoruns-add"
            onClick={() => {
              setFormError(null)
              setForm({ draft: emptyDraft(), editing: null })
            }}
          >
            {t('autoruns.add', undefined, { defaultValue: 'Add autorun' })}
          </Button>
        )}
      </Flex>

      {!sandboxRunning ? (
        <PanelNotice
          tone="warning"
          molId="autoruns-sandbox-off"
          action={
            onWakeSandbox && canEdit ? (
              <Button
                size="sm"
                variant="outline"
                className={cm.touchTargetCompact}
                data-mol-id="autoruns-wake"
                onClick={onWakeSandbox}
              >
                {t('autoruns.wake', undefined, { defaultValue: 'Start sandbox' })}
              </Button>
            ) : undefined
          }
        >
          {t('autoruns.sandboxOff', undefined, {
            defaultValue: 'The autoruns live in the sandbox. Start it to see and change them.',
          })}
        </PanelNotice>
      ) : !hooks ? (
        <Flex justify="center" style={{ padding: 16 }}>
          <MoleculeSpinner size={28} />
        </Flex>
      ) : (
        <>
          {form && (
            <HookForm
              draft={form.draft}
              onChange={(draft) => setForm({ ...form, draft })}
              checkSteps={hooks.checkSteps}
              editing={form.editing !== null}
              busy={busy === 'form'}
              error={formError}
              onSave={() => void saveForm()}
              onCancel={() => setForm(null)}
            />
          )}
          {hooks.invalidFiles.length > 0 && (
            <PanelNotice tone="warning" molId="autoruns-invalid">
              {t(
                'autoruns.invalidFile',
                { files: hooks.invalidFiles.join(', ') },
                {
                  defaultValue:
                    '{{files}} is not valid JSON, so its hooks are ignored. Fix it in the editor.',
                },
              )}
            </PanelNotice>
          )}
          {rows.autoruns.length === 0 ? (
            <MutedNote molId="autoruns-empty">
              {t('autoruns.empty', undefined, {
                defaultValue:
                  'No autoruns yet. Add one to run a check after each deploy, task or turn.',
              })}
            </MutedNote>
          ) : (
            rows.autoruns.map(renderRow)
          )}
          {rows.tools.length > 0 && (
            <>
              <SectionHeading molId="autoruns-tools-heading">
                {t('autoruns.toolHooks', undefined, { defaultValue: 'Tool hooks' })}
              </SectionHeading>
              {rows.tools.map(renderRow)}
            </>
          )}
        </>
      )}

      <SectionHeading molId="autoruns-running-heading">
        {t('autoruns.runningNow', undefined, { defaultValue: 'Running now' })}
      </SectionHeading>
      {running.length === 0 && liveSessions.length === 0 ? (
        <MutedNote molId="autoruns-running-empty">
          {t('autoruns.nothingRunning', undefined, { defaultValue: 'Nothing is running.' })}
        </MutedNote>
      ) : (
        <div data-mol-id="autoruns-running">
          {liveSessions.map((s) => (
            <Flex
              key={s.conversationId}
              align="center"
              gap="sm"
              data-mol-id={`autoruns-session-${s.conversationId}`}
              style={{ padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}
            >
              <Chip tone="byo" molId={`autoruns-session-chip-${s.conversationId}`}>
                {t('autoruns.session', undefined, { defaultValue: 'Session' })}
              </Chip>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {s.title}
                {s.working ? <span className={cm.textMuted}>{` — ${s.working}`}</span> : null}
              </span>
            </Flex>
          ))}
          {running.map((item) => (
            <Flex
              key={item.id}
              align="center"
              gap="sm"
              data-mol-id={`autoruns-live-${item.id}`}
              style={{ padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}
            >
              <Chip
                tone={
                  item.status === 'running'
                    ? 'byo'
                    : item.status === 'succeeded'
                      ? 'configured'
                      : 'warning'
                }
                molId={`autoruns-live-chip-${item.id}`}
              >
                {item.status === 'running'
                  ? t('autoruns.live.running', undefined, { defaultValue: 'Running' })
                  : item.status === 'succeeded'
                    ? t('autoruns.live.done', undefined, { defaultValue: 'Done' })
                    : t('autoruns.live.failed', undefined, { defaultValue: 'Failed' })}
              </Chip>
              <span className={cm.textMuted} style={{ fontSize: 11 }}>
                {item.kind === 'agent'
                  ? t('autoruns.live.agent', undefined, { defaultValue: 'agent' })
                  : t('autoruns.live.command', undefined, { defaultValue: 'command' })}
              </span>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <Mono>{item.label}</Mono>
              </span>
            </Flex>
          ))}
        </div>
      )}

      <SectionHeading molId="autoruns-scheduled-heading">
        {t('autoruns.scheduled', undefined, { defaultValue: 'Scheduled' })}
      </SectionHeading>
      {scheduled.length === 0 ? (
        <MutedNote molId="autoruns-scheduled-empty">
          {t('autoruns.nothingScheduled', undefined, { defaultValue: 'Nothing is scheduled.' })}
        </MutedNote>
      ) : (
        scheduled.map((task) => (
          <Flex
            key={task.id}
            align="center"
            gap="sm"
            data-mol-id={`autoruns-scheduled-${task.id}`}
            style={{ padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}
          >
            <Chip
              tone={task.status === 'running' ? 'byo' : 'none'}
              molId={`autoruns-scheduled-chip-${task.id}`}
            >
              {task.status === 'running'
                ? t('autoruns.live.running', undefined, { defaultValue: 'Running' })
                : task.mode === 'idle'
                  ? t('autoruns.whenIdle', undefined, { defaultValue: 'When idle' })
                  : task.runAt
                    ? new Date(task.runAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : t('autoruns.queued', undefined, { defaultValue: 'Queued' })}
            </Chip>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {task.prompt}
            </span>
            {canEdit && task.status === 'queued' && (
              <Button
                size="sm"
                variant="ghost"
                color="error"
                className={cm.touchTargetCompact}
                data-mol-id={`autoruns-scheduled-cancel-${task.id}`}
                disabled={busy !== null}
                onClick={() =>
                  void act(`cancel-${task.id}`, () => cancelScheduledTask(http, projectId, task.id))
                }
              >
                {t('common.cancel', undefined, { defaultValue: 'Cancel' })}
              </Button>
            )}
          </Flex>
        ))
      )}
    </div>
  )
}
